import type { IconData } from "@loot-os/icons/types";
import type { ReactNode } from "react";
import { Badge } from "../core/Badge.js";
import { Icon } from "../core/Icon.js";
import { Text } from "../core/Text.js";
import { cn } from "../foundations/cn.js";
import { useLayout } from "../foundations/themeContext.js";
import { Stage } from "../moments/Stage.js";

export interface HeroProps {
  /** One short label above the title: NEW THIS WEEK · WATER TYPES. */
  eyebrow?: string;
  /** The page's one display-hero headline. */
  title: string;
  body?: string;
  /** At most one brand button and one secondary action. */
  actions?: ReactNode;
  /** Two or three proof points under the actions. */
  proof?: readonly { icon: IconData; label: string }[];
  /** The art on the right: fanned products, a box, a figure. */
  media?: ReactNode;
  /** `split`: text beside the art. `centered`: text centered, the art beneath it. */
  align?: "split" | "centered";
  className?: string;
}

/**
 * The top of Home on the theme's stage: eyebrow, headline, one line, the main action and proof
 * points, with art beside them. The floating nav passes over it.
 */
export function Hero({
  eyebrow,
  title,
  body,
  actions,
  proof,
  media,
  align = "split",
  className,
}: HeroProps) {
  const centered = align === "centered";
  const layout = useLayout();
  return (
    <section
      className={cn(
        "relative isolate overflow-hidden pt-28 pb-14 md:pb-20",
        // A floating nav sits over the stage, so the hero clears it; an attached one is in the flow.
        layout.header === "floating" ? "md:pt-40" : "md:pt-16",
        className,
      )}
    >
      <Stage className="-z-10" />
      <div
        className={cn(
          "mx-auto grid w-full max-w-[1440px] items-center gap-10 px-4 md:px-8 lg:px-20",
          !centered && "md:grid-cols-[1fr_1fr]",
        )}
      >
        <div
          className={cn(
            "flex flex-col gap-6",
            centered ? "items-center text-center" : "items-start",
          )}
        >
          {eyebrow && <Badge tone="accent">{eyebrow}</Badge>}
          <Text
            as="h1"
            variant="display-hero"
            tone="primary"
            className={cn(
              "max-md:text-display-xl2 md:max-lg:text-display-xl1",
              !centered && "max-w-[560px]",
            )}
          >
            {title}
          </Text>
          {body && (
            <Text
              variant="body-b1"
              tone="secondary"
              className={cn("max-w-md md:text-heading-h2 md:font-normal", centered && "max-w-xl")}
            >
              {body}
            </Text>
          )}
          {actions && (
            <div className={cn("flex flex-wrap gap-3", centered && "justify-center")}>
              {actions}
            </div>
          )}
          {proof && (
            <ul className={cn("flex flex-wrap gap-x-6 gap-y-2", centered && "justify-center")}>
              {proof.map((item) => (
                <li
                  key={item.label}
                  className="flex items-center gap-2 font-body text-body-b3 text-text-secondary"
                >
                  <Icon icon={item.icon} size={14} className="text-accent-highlight" />
                  {item.label}
                </li>
              ))}
            </ul>
          )}
        </div>
        {media && (
          <div className={cn("relative flex justify-center", !centered && "md:justify-end")}>
            {media}
          </div>
        )}
      </div>
    </section>
  );
}
